// src/app/core/interceptors/auth.interceptor.ts
import {
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpEvent,
  HttpClient,
  HttpContextToken,
  HttpContext,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { throwError, Observable, BehaviorSubject } from 'rxjs';
import { environment } from '@src/environments/environment';
import Swal from 'sweetalert2';
import { TokenService } from '@app/services/token.service';

// Si tienes tu interface genérica ya creada, reutilízala
export interface GenericResponse<T = any> {
  rpta: number; // 1 ok, 0 error
  msg: string; // mensaje amigable
  data: T | null; // puede ser un objeto, array, o Map de validaciones
}

/** Contexto para saltar el interceptor (p.ej. refresh-token) */
export const BYPASS_INTERCEPTOR = new HttpContextToken(() => false);

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshingToken = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private router: Router,
    private httpClient: HttpClient,
    private tokenService: TokenService
  ) { }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (req.context.get(BYPASS_INTERCEPTOR) === true) {
      return next.handle(req);
    }

    const token = localStorage.getItem('token');
    const authReq = token ? this.addTokenHeader(req, token) : req;

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // Una escritura auditada nunca se reenvía ni se transforma en éxito.
        if (authReq.url.startsWith(environment.urlAsistencias + 'sgs-observabilidad/api/usuarios-auditoria/')) {
          return throwError(() => error);
        }
        // El rechazo de la API auxiliar de metricas no demuestra que haya
        // caducado la sesion Java del CRM. Se muestra el error sin renovar,
        // repetir la consulta ni borrar la sesion principal.
        if ((error.status === 401 || error.status === 403) &&
            this.isMetricasBasesRequest(authReq)) {
          return throwError(() => error);
        }

        // 401 → intenta refresh si no es login
        if (error.status === 401 && !this.isLoginRequest(authReq)) {
          return this.handle401Error(authReq, next);
        }

        // 🔥 400 → Verificar si es un error de token inválido del backend
        if (error.status === 400 && !this.isLoginRequest(authReq) && error.error?.rpta === 0) {
          const errorMsg = error.error.msg || '';

          // Si el mensaje indica token inválido/expirado, intentar renovar
          if (
            errorMsg.includes('inválido') ||
            errorMsg.includes('invalido') ||
            errorMsg.includes('expirado') ||
            errorMsg.includes('Token')
          ) {
            console.log('⚠️ Error 400 con token inválido detectado, intentando renovar...');
            return this.handle401Error(authReq, next);
          }
        }

        // 403 → token expirado o sin permisos, limpiar sesión
        if (error.status === 403 && !this.isLoginRequest(authReq)) {
          // Solo mostrar modal si había una sesión válida
          if (this.tokenService.hasValidSession()) {
            this.handleLogout();
          } else {
            // Si no hay sesión válida, limpiar silenciosamente
            this.tokenService.silentLogout();
            this.router.navigate(['/auth/login']);
          }
          return throwError(() => error);
        }

        // Si es una petición de login, NO mostrar SweetAlert (se maneja en el componente)
        if (this.isLoginRequest(authReq)) {
          return throwError(() => error);
        }

        // Si es una petición de conversión de audio, NO mostrar SweetAlert (se maneja en el componente)
        if (this.isAudioConversionRequest(authReq)) {
          return throwError(() => error);
        }

        // Si es una petición de registro de usuario, NO mostrar SweetAlert (se maneja en el componente)
        if (this.isUserRegistrationRequest(authReq)) {
          return throwError(() => error);
        }

        // Si es una petición de FAQs con error 404, NO mostrar SweetAlert (lista vacía es válido)
        if (this.isFaqRequest(authReq) && error.status === 404) {
          return throwError(() => error);
        }

        // 404 = No hay datos, no mostrar error (se maneja en cada componente)
        if (error.status === 404) {
          return throwError(() => error);
        }

        // Para el resto (400/409/422/500...), parseamos y mostramos
        const parsed = this.parseServerError(error);
        this.showErrorAlert(parsed);

        return throwError(() => error);
      })
    );
  }

  // ===================== REFRESH TOKEN =====================

  private handle401Error(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      this.handleLogout();
      return throwError(() => new Error('No hay token para renovar.'));
    }

    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.refreshTokenSubject.next(null);

      const context = new HttpContext().set(BYPASS_INTERCEPTOR, true);
      const refreshTokenUrl = `${environment.url}api/authentication/refresh-token`;

      return this.httpClient
        .post<GenericResponse<{ token: string }>>(refreshTokenUrl, null, {
          context,
          // BYPASS_INTERCEPTOR tambien omite addTokenHeader: enviarlo aqui.
          headers: { Authorization: `Bearer ${currentToken}` },
        })
        .pipe(
          switchMap((response) => {
            this.isRefreshingToken = false;
            if (response.rpta === 1 && response.data?.token) {
              const newToken = response.data.token;
              localStorage.setItem('token', newToken);
              this.refreshTokenSubject.next(newToken);
              return next.handle(this.addTokenHeader(req, newToken));
            }
            // Si rpta !== 1, el token no se pudo renovar
            console.error('❌ No se pudo renovar el token:', response.msg);
            this.handleLogout();
            return throwError(() => new Error('No se pudo renovar el token.'));
          }),
          catchError((err: HttpErrorResponse) => {
            this.isRefreshingToken = false;

            // 🔥 Manejo especial para error 400 del backend (token inválido)
            if (err.status === 400 && err.error?.rpta === 0) {
              const errorMsg = err.error.msg || '';
              console.error('❌ Error al renovar token (400):', errorMsg);

              // Verificar si es un error de token inválido/expirado
              if (
                errorMsg.includes('inválido') ||
                errorMsg.includes('invalido') ||
                errorMsg.includes('expirado') ||
                errorMsg.includes('Token')
              ) {
                console.error('❌ Token no se puede renovar. Cerrando sesión.');
                this.handleLogout();
                return throwError(() => new Error('Token no se puede renovar'));
              }
            }

            // Para otros errores, también cerrar sesión
            console.error('❌ Error al renovar token:', err);
            this.handleLogout();
            return throwError(() => err);
          })
        );
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((jwt) => next.handle(this.addTokenHeader(req, jwt)))
    );
  }

  private handleLogout(): void {
    this.isRefreshingToken = false;
    this.refreshTokenSubject.next(null);

    this.tokenService.clearAuthData();
    this.router.navigate(['/auth/login']);
    Swal.fire({
      icon: 'warning',
      title: 'Sesión expirada',
      text: 'Tu sesión ha finalizado. Inicia sesión nuevamente.',
      confirmButtonText: 'Ir a login',
    });
  }

  private addTokenHeader(
    request: HttpRequest<any>,
    token: string | null
  ): HttpRequest<any> {
    if (!token) return request;
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`),
    });
  }

  private isLoginRequest(request: HttpRequest<any>): boolean {
    const loginEndpoints = [
      '/api/authentication/sign-in',
      '/api/authentication/signin',
      '/api/auth/login',
      '/api/auth/signin',
    ];
    return loginEndpoints.some((ep) => request.url.includes(ep));
  }

  private isMetricasBasesRequest(request: HttpRequest<any>): boolean {
    if (request.method !== 'GET') return false;
    const ruta = request.url.split('?')[0];
    if (!ruta.startsWith(environment.urlVentasMetricas)) return false;
    const path = ruta.slice(environment.urlVentasMetricas.length);
    return [
      'cola-llamadas/resumen-cargas/',
      'cola-llamadas/evolucion-horas/',
    ].includes(path) || /^cola-llamadas\/registros-actualizados\/\d+\/$/.test(path);
  }

  private isAudioConversionRequest(request: HttpRequest<any>): boolean {
    const audioConversionEndpoints = [
      '/api/audio-conversion/gsm-to-mp3',
      '/api/audio-conversion/',
    ];
    return audioConversionEndpoints.some((ep) => request.url.includes(ep));
  }

  private isUserRegistrationRequest(request: HttpRequest<any>): boolean {
    const registrationEndpoints = [
      '/api/user/registrar',
      '/api/user/register',
      '/api/authentication/sign-up',
      '/api/authentication/signup',
    ];
    return registrationEndpoints.some((ep) => request.url.includes(ep));
  }

  private isFaqRequest(request: HttpRequest<any>): boolean {
    const faqEndpoints = [
      '/api/faqs',
      '/api/faq-respuestas',
    ];
    return faqEndpoints.some((ep) => request.url.includes(ep));
  }

  // ===================== PARSEO DE ERRORES =====================

  /** Convierte un HttpErrorResponse en título + mensaje (+ lista de validaciones si llega) */
  private parseServerError(err: HttpErrorResponse): {
    title: string;
    message: string;
    validationsHtml?: string;
  } {
    const status = err.status;
    const title = this.titleForStatus(status);

    // 1) Si el backend te mandó tu GenericResponse<T>
    const maybeGR: GenericResponse<any> | null = this.asGenericResponse(
      err.error
    );
    if (maybeGR) {
      // Mensaje principal
      const msg = (maybeGR.msg ?? 'Ocurrió un error').toString();

      // Validaciones: data puede ser un mapa { campo: mensaje }
      let validationsHtml: string | undefined;
      if (maybeGR.data && this.isPlainObject(maybeGR.data)) {
        const entries = Object.entries(maybeGR.data as Record<string, string>);
        if (entries.length) {
          validationsHtml = `
            <ul style="text-align:left; margin-top:8px">
              ${entries
              .map(
                ([field, message]) =>
                  `<li><b>${this.escapeHtml(field)}:</b> ${this.escapeHtml(
                    String(message)
                  )}</li>`
              )
              .join('')}
            </ul>
          `;
        }
      }
      return { title, message: msg, validationsHtml };
    }

    // 2) Si vino otra forma de JSON estándar
    if (this.isPlainObject(err.error)) {
      const anyObj = err.error as any;
      const msg =
        anyObj.msg || anyObj.message || err.message || 'Ocurrió un error';
      return { title, message: String(msg) };
    }

    // 3) Si vino string (text/plain, HTML, etc.)
    if (typeof err.error === 'string') {
      return { title, message: err.error };
    }

    // 4) Si vino Blob u otra cosa no serializable
    return { title, message: err.message || 'Ocurrió un error' };
  }

  private titleForStatus(status: number): string {
    switch (status) {
      case 400:
        return 'Solicitud inválida';
      case 401:
        return 'No autorizado';
      case 403:
        return 'Acceso denegado';
      case 404:
        return 'No encontrado';
      case 409:
        return 'Conflicto de datos';
      case 422:
        return 'Datos inválidos';
      case 500:
        return 'Error del servidor';
      default:
        return `Error (${status})`;
    }
  }

  private asGenericResponse(payload: any): GenericResponse<any> | null {
    if (!payload || typeof payload !== 'object') return null;
    const hasShape =
      Object.prototype.hasOwnProperty.call(payload, 'rpta') &&
      Object.prototype.hasOwnProperty.call(payload, 'msg') &&
      Object.prototype.hasOwnProperty.call(payload, 'data');
    return hasShape ? (payload as GenericResponse<any>) : null;
  }

  private isPlainObject(v: any): v is Record<string, any> {
    return v !== null && typeof v === 'object' && v.constructor === Object;
  }

  private escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // ===================== ALERTA =====================

  private showErrorAlert(opts: {
    title: string;
    message: string;
    validationsHtml?: string;
  }) {
    const { title, message, validationsHtml } = opts;

    if (validationsHtml) {
      Swal.fire({
        icon: 'error',
        title,
        html: `<div>${this.escapeHtml(message)}</div>${validationsHtml}`,
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#ef4444',
      });
    } else {
      Swal.fire({
        icon: 'error',
        title,
        text: message,
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#ef4444',
      });
    }
  }

  /** Útil cuando quieres resetear estado al loguearte */
  public resetInterceptorState(): void {
    this.isRefreshingToken = false;
    this.refreshTokenSubject.next(null);
  }

  /** Valida si el token JWT está expirado */
  public isTokenExpired(token: string): boolean {
    return this.tokenService.isTokenExpired(token);
  }

  /** Limpia la sesión si el token está expirado */
  public checkAndCleanExpiredToken(): void {
    // Si no hay sesión válida, no hacer nada
    if (!this.tokenService.hasValidSession()) {
      return;
    }

    if (!this.tokenService.validateAndCleanToken()) {
      console.warn('🔒 Token expirado detectado, limpiando sesión...');
      // Solo mostrar modal si había una sesión válida
      this.handleLogout();
    }
  }
}
